import { FormInstance, notification } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import dayjs, { Dayjs } from 'dayjs';
import { ILangValue, Language } from 'mixins/types';
import React, { ReactElement } from 'react';
import { TaxpayerInterface } from '../parts/admin-token-generations/admin-token-generations';
import { lettersOnlyPattern } from './constants';
import { DataFromEcp } from '/react4xp/parts/block-auth/block-auth.type';

export const capitalizeFirstLetter = (value: string) => {
    return value.charAt(0).toUpperCase() + value.slice(1);
};

export const getNameByLang = (data, currentLang = 'ru', name = 'name'): string => {
    return data && data[getTitleByLang(name, currentLang)];
};

export const localize = (
    needle: string,
    translations: Record<string, string>,
    values?: Record<string, any>,
    payload?: string
): string => {
    if (translations) {
        let phrase = translations[needle];

        if (values) {
            Object.keys(values).forEach((x) => {
                phrase = phrase.replace(`{${x}}`, values[x]);
            });
        }

        if (payload) {
            return (phrase || needle).replace('__CODE__', payload);
        }

        return phrase || needle;
    }

    return needle;
};

export const getLangValue = (lang: string): ILangValue => {
    switch (lang) {
        case 'en_US':
            return 'en';
        case 'kk_KZ':
            return 'kk';
        case 'en_150':
            return 'qq';
    }

    return lang;
};

export const getLangValueForCalendar = (lang: string): ILangValue => {
    switch (lang) {
        case 'en_US':
            return 'en';
        case 'kk_KZ':
            return 'kk';
        case 'en_150':
            return 'kk';
    }

    return lang;
};

export const getLangValueId = (lang: string): ILangValue => {
    switch (lang) {
        case 'en':
            return 'en-US';
        case 'kk':
            return 'kk-KZ';
        case 'qq':
            return 'en-150';
    }

    return lang;
};

export const getTitleByLang = (name: string, currentLang = 'ru'): string => {
    return name + capitalizeFirstLetter(getLangValue(currentLang));
};

export const saveFile = (data: Blob, type: string, title = 'template.xlsx') => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = title;
    link.click();
    link.remove();
};

export const removeEmptyValues = (obj: Record<string, any>): Record<string, any> => {
    Object.keys(obj).forEach((key) => {
        if (obj[key] === '' || obj[key] === undefined) {
            delete obj[key];
        }
    });

    return obj;
};

export const formatServerTime = (res: string): string => {
    return dayjs(res).format('HH:mm DD.MM.YYYY');
};

export const formattedTableTitles = (
    data: any,
    serviceName: string,
    translations: Record<string, string>
) => {
    data.map(
        (
            x: { title: string; children: [{ title: string; children: [{ title: string }] }] },
            id: number
        ) => {
            if (x.children) {
                x.children.map((child, colId) => {
                    child.title = localize(
                        `${serviceName}.col-${id}-children-${colId}`,
                        translations
                    );
                    if (x.children[colId].children) {
                        x.children[colId].children.map(
                            (child, childId) =>
                                (child.title = localize(
                                    `${serviceName}.col-${id}-children-${colId}-grandchildren-${childId}`,
                                    translations
                                ))
                        );
                    }
                });
            }

            x.title = localize(`${serviceName}.col-${id}`, translations);
        }
    );

    return data;
};

export function parseUrlQuery() {
    const data = {};

    if (location.search) {
        const pair = location.search.substr(1).split('&');

        for (let i = 0; i < pair.length; i++) {
            const param = pair[i].split('=');
            data[param[0]] = param[1];
        }
    }
    return data;
}

export function filterDigits(form: FormInstance, field: NamePath) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (/^\d*$/.test(value)) return;
        form.setFieldValue(field, [...value].filter((char) => /\d/.test(char)).join(''));
    };
}

export function filterDigitsWithUpperCaseLetters(form: FormInstance, field: NamePath) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (/^[A-Z0-9]*$/.test(value)) return;
        form.setFieldValue(field, [...value].filter((char) => /[A-Z0-9]/.test(char)).join(''));
    };
}

export function filterLetters(form: FormInstance, field: NamePath) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (lettersOnlyPattern.test(value)) return;
        form.setFieldValue(
            field,
            [...value].filter((char) => lettersOnlyPattern.test(char)).join('')
        );
    };
}

export const getFileName = (disposition: string): string => {
    const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\.]+)(?:; ?|$)/i;
    const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

    let fileName: string = null;
    if (utf8FilenameRegex.test(disposition)) {
        fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)[1]);
    } else {
        if (disposition) {
            const filenameStart = disposition.toLowerCase().indexOf('filename=');
            if (filenameStart >= 0) {
                const partialDisposition = disposition.slice(filenameStart);
                const matches = asciiFilenameRegex.exec(partialDisposition);
                if (matches != null && matches[2]) {
                    fileName = matches[2];
                }
            }
        }
    }
    return fileName;
};

export const stripHtml = (html: string) => {
    if (typeof document !== 'undefined') {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    return html;
};

export const getValueFromLanguageSet = (name: any, lang: string) => {
    switch (lang) {
        case 'en_US':
            if (name.nameEn) return name.nameEn;
            if (name.en) return name.en;
        case 'en':
            if (name.nameEn) return name.nameEn;
            if (name.en) return name.en;
        case 'eng':
            if (name.nameEn) return name.nameEn;
            if (name.en) return name.en;
        case 'kk_KZ':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'en_150':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'qq':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'kz':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'kk':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'kaz':
            if (name.nameKk) return name.nameKk;
            if (name.kk) return name.kk;
        case 'ru':
            if (name.nameRu) return name.nameRu;
            if (name.ru) return name.ru;
        case 'rus':
            if (name.nameRu) return name.nameRu;
            if (name.ru) return name.ru;
    }

    return '_error_title_';
};

export const getLangValueParams = (lang: string): string => {
    switch (lang) {
        case 'en_US':
            return 'en';
        case 'kk_KZ':
            return 'kaz';
        case 'en_150':
            return 'qq';
        default:
            return 'rus';
    }
};

export const getLinkRow = (title: string, url: string, links: ReactElement): ReactElement => {
    return (
        <>
            {links}
            <div>
                <a
                    href={url}
                    target="_blank"
                >
                    {title}
                </a>
            </div>
        </>
    );
};

export const getLinks = (config, translations): ReactElement => {
    let links = null;

    if (config.passport) {
        links = getLinkRow(localize('view-rules', translations), config.passport, links);
    }

    if (config.knp) {
        links = getLinkRow(localize('get-service-tpc', translations), config.knp, links);
    }

    if (config.egov) {
        links = getLinkRow(localize('get-service-egov', translations), config.egov, links);
    }

    if (config.form) {
        links = getLinkRow('Скачать форму заявления', config.form, links);
    }

    return links;
};

export const isHidden = (hidden: boolean) => {
    if (hidden) {
        return 'hidden';
    }

    return '';
};

export function filterFloats(
    form: FormInstance,
    field: NamePath,
    integer: number,
    decimal: number
) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = event.target;
        const totalLength = integer + decimal + 1;
        value = value.replace(/[^\d.]/g, '');
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        if (value.length > totalLength) {
            value = value.slice(0, totalLength);
        }
        form.setFieldValue(field, value);
    };
}

export function removeLastPathSegment(url) {
    const urlParts = url.split('/');
    urlParts.pop();
    const newUrl = urlParts.join('/');
    return newUrl;
}

export const getRandomSecure = () => {
    let array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0] / (0xffffffff + 1);
};

export const guidGenerator = () => {
    const S4 = () => {
        return (((1 + getRandomSecure()) * 0x10000) | 0).toString(16).substring(1);
    };

    return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
};

export const convertToArray = (arr) => {
    if (Array.isArray(arr)) {
        return arr;
    }

    return arr ? [arr] : [];
};

export const validatePrivileges = (
    roles: string[],
    privileges: string[],
    priv: string
): boolean => {
    if (
        (roles && roles.includes('role:system.admin.login')) ||
        (privileges && privileges.includes(priv))
    ) {
        return true;
    }

    return false;
};

export const getExt = (filename: string): string => {
    return filename.split('.').pop();
};

export function getTaxpayerNameByLang(name: TaxpayerInterface['name'], lang: Language) {
    switch (lang) {
        case Language.qq:
            return name.qq;
        case Language.ru:
            return name.ru;
        case Language.kz:
            return name.kk;
        case Language.en:
            return name.en;

        default:
            return name.ru;
    }
}

export function getFullName(lastName?: string, firstName?: string, middleName?: string): string {
    let fullName = '';

    if (lastName) {
        fullName += lastName + ' ';
    }

    if (firstName) {
        fullName += firstName + ' ';
    }

    if (middleName) {
        fullName += middleName;
    }

    return fullName;
}

export const edsResultCheck = (data: DataFromEcp, translations: any): string => {
    if (data.result === 'EXPIRED') {
        notification['error']({
            message: localize('error', translations),
            description: localize('error.eds-expired', translations),
        });
    } else {
        const iinBin = data.subjectInfo && (data.subjectInfo.iin || data.subjectInfo.bin);

        if (iinBin) {
            return iinBin;
        } else {
            notification['error']({
                message: localize('error', translations),
                description: localize('block-auth-iin-bin-not-found', translations),
            });
        }
    }

    return null;
};

export const handleInputKeyDownOnlyDigit = (e) => {
    const { key } = e;

    // Разрешаем цифры, одну точку и клавиши управления (Backspace, Delete, Tab, и т.д.)
    if (
        !/[0-9]/.test(key) && // Разрешить цифры
        key !== '.' && // Разрешить одну точку
        key !== 'Backspace' &&
        key !== 'Delete' &&
        key !== 'ArrowLeft' &&
        key !== 'ArrowRight' &&
        key !== 'Tab'
    ) {
        e.preventDefault();
    }

    // Запретить ввод более одной точки
    if (key === '.' && e.currentTarget.value.includes('.')) {
        e.preventDefault();
    }

    // Запрещаем ввод более чем 2-х знаков после запятой
    if (
        e.currentTarget.value.includes('.') &&
        e.currentTarget.value.split('.')[1].length >= 2 &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)
    ) {
        e.preventDefault();
    }
};

export const handleInputOnChangeOnlyDigit = (e) => {
    const value = e.target.value;

    // Запрещаем ввод более чем 2-х знаков после запятой
    if (value.includes('.') && value.split('.')[1].length > 2) {
        e.preventDefault();
    }
};

export const handleInputKeyDownYear = (e) => {
    const { key, currentTarget } = e;
    const value = currentTarget.value;

    // Разрешаем цифры и клавиши управления (Backspace, Delete, стрелки, Tab)
    if (
        !/[0-9]/.test(key) &&
        key !== 'Backspace' &&
        key !== 'Delete' &&
        key !== 'ArrowLeft' &&
        key !== 'ArrowRight' &&
        key !== 'Tab'
    ) {
        e.preventDefault();
    }

    // Запрещаем ввод, если длина уже 4 символа и это не управляющая клавиша
    if (
        value.length >= 4 &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)
    ) {
        e.preventDefault();
    }
};

export const handleInputOnChangeYear = (e) => {
    const value = e.target.value;

    // Ограничиваем ввод до 4 символов
    if (value.length > 4) {
        e.preventDefault();
    }
};

export const handleInputKeyDownPhone = (e) => {
    const { key } = e;

    // Разрешаем цифры, одну точку и клавиши управления (Backspace, Delete, Tab, и т.д.)
    if (
        !/[0-9]/.test(key) && // Разрешить цифры
        key !== '.' && // Разрешить одну точку
        key !== 'Backspace' &&
        key !== 'Delete' &&
        key !== 'ArrowLeft' &&
        key !== 'ArrowRight' &&
        key !== 'Tab'
    ) {
        e.preventDefault();
    }

    // Запретить ввод более одной точки
    if (key === '.' && e.currentTarget.value.includes('.')) {
        e.preventDefault();
    }

    // Запрещаем ввод более чем 11-х знаков после запятой
    if (
        e.currentTarget.value.includes('.') &&
        e.currentTarget.value.split('.')[1].length >= 11 &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)
    ) {
        e.preventDefault();
    }
};

export const handleInputOnChangePhone = (e) => {
    const value = e.target.value;

    // Ограничиваем ввод до 11 символов
    if (value.length > 11) {
        e.preventDefault();
    }
};

export const numberSeparator = (n: number) => {
    const numberFormatter = Intl.NumberFormat('ru-RU');

    return numberFormatter.format(n);
};

export const getNumberSeparator = (value) => {
    return value && numberSeparator(parseFloat(value.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]));
};

export const formatDate = (date: string | null): string => {
    return (date && dayjs(date).format('DD.MM.YYYY')) || '-';
};

type RangeDisabledDate = (current: Dayjs, info: { from?: Dayjs }) => boolean;
export const disableDatesBeforeFrom: RangeDisabledDate = (current, info) => {
    if (info.from) {
        return current.isBefore(info.from, 'day');
    }
    return false;
};
